import React, { useState } from 'react';
import { Check, ChevronRight, RotateCcw, ArrowRight } from 'lucide-react';
import PropTypes from 'prop-types';
import { useMutation } from '@apollo/client';

import { C, F } from '../constants/theme';
import { Btn, Brandmark, Input, Tag } from '../components';
import { SIGN_UP } from 'core/api/apollo/mutations';
import cacheUpdater, { updateUserQuery } from 'core/api/cache/updaters';
import { useProgramContext } from 'core/api/programContext';

const Phase0Landing = ({ goPhase }) => {
  const { resetQuizState } = useProgramContext();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [zip, setZip] = useState('');
  const [zipCodeErrors, setZipCodeErrors] = useState(false);
  const [showExistingUserDialog, setShowExistingUserDialog] = useState(false);
  const [showAlreadyHasAccount, setShowAlreadyHasAccount] = useState(false);

  const onSignUp = ({ signUp }) => {
    if (signUp.errors && signUp.errors.length > 0) {
      if (signUp.errors[0].message === 'You already have an account') {
        setShowAlreadyHasAccount(true);
        return;
      }
      setZipCodeErrors(signUp.errors[0].message);
    } else if (signUp.existingUser) {
      setShowExistingUserDialog(true);
    } else if (signUp.user && signUp.user.primaryAddress && signUp.user.primaryAddress.supported) {
      resetQuizState();
      goPhase(1);
    } else {
      setZipCodeErrors('Sorry, we don\'t currently deliver to your zip code.');
    }
  };

  const handleContinue = () => {
    setShowExistingUserDialog(false);
    goPhase(1);
  };

  const handleStartFresh = () => {
    setShowExistingUserDialog(false);
    resetQuizState();
    goPhase(1);
  };

  const [signUpWithZipCode] = useMutation(SIGN_UP, {
    variables: { input: { zipCode: zip, firstName: firstName, lastName: lastName, email: email } },
    skip: !zip,
    update: cacheUpdater('signUp', updateUserQuery),
    onCompleted: onSignUp,
  });

  const handleSignUp = () => {
    if (zip && firstName && email) {
      signUpWithZipCode({
        input: { zipCode: zip, firstName: firstName, lastName: lastName, email },
      });
    }
  };

  if (showAlreadyHasAccount) {
    return <>
      <div style={{ textAlign: 'center', marginBottom: 32 }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 0 }}>
          <Brandmark size={36} color={C.lightGreen} />
        </div>
        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '2px', color: C.green, marginTop: 16, marginBottom: 10 }}>
          ACCOUNT EXISTS
        </div>
        <h1 style={{ fontFamily: F.h, fontSize: 28, fontWeight: 500, color: C.darkBrown, lineHeight: 1.15, margin: '0 0 12px', textWrap: 'balance' }}>
          You already have an account.
        </h1>
        <p style={{ fontSize: 14, color: C.brown, lineHeight: 1.6, maxWidth: 440, margin: '0 auto 28px' }}>
          Please sign in with your existing credentials to continue.
        </p>
        <a href="/sign-in" style={{
          display: 'inline-flex', alignItems: 'center', gap: 8, padding: '14px 32px',
          background: C.green, color: C.pureWhite, border: 'none', borderRadius: 0,
          cursor: 'pointer', textAlign: 'center', fontFamily: F.b, fontSize: 14, fontWeight: 600,
          textDecoration: 'none', transition: 'all 0.15s',
        }}>
          Sign In <ArrowRight size={16} />
        </a>
      </div>
    </>;
  }

  if (showExistingUserDialog) {
    return <>
      <div style={{ textAlign: 'center', marginBottom: 32 }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 0 }}>
          <Brandmark size={36} color={C.lightGreen} />
        </div>
        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '2px', color: C.green, marginTop: 16, marginBottom: 10 }}>
          WELCOME BACK
        </div>
        <h1 style={{ fontFamily: F.h, fontSize: 28, fontWeight: 500, color: C.darkBrown, lineHeight: 1.15, margin: '0 0 12px', textWrap: 'balance' }}>
          Looks like you've been here before.
        </h1>
        <p style={{ fontSize: 14, color: C.brown, lineHeight: 1.6, maxWidth: 440, margin: '0 auto 28px' }}>
          We found a plan in progress for <strong>{email}</strong>. How would you like to proceed?
        </p>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <button onClick={handleContinue} style={{
          display: 'flex', alignItems: 'center', gap: 14, padding: '18px 20px', width: '100%',
          background: C.optimalBg, border: `1.5px solid ${C.green}`, borderRadius: 0,
          cursor: 'pointer', textAlign: 'left', fontFamily: F.b, transition: 'all 0.15s',
        }}>
          <ArrowRight size={20} color={C.green} style={{ flexShrink: 0 }} />
          <div>
            <div style={{ fontSize: 14, fontWeight: 600, color: C.darkBrown }}>Continue where you left off</div>
            <div style={{ fontSize: 12, color: C.brown, marginTop: 2 }}>Pick up your plan right where you stopped.</div>
          </div>
        </button>
        <button onClick={handleStartFresh} style={{
          display: 'flex', alignItems: 'center', gap: 14, padding: '18px 20px', width: '100%',
          background: C.pureWhite, border: '1.5px solid #E8E4DC', borderRadius: 0,
          cursor: 'pointer', textAlign: 'left', fontFamily: F.b, transition: 'all 0.15s',
        }}>
          <RotateCcw size={18} color={C.brown} style={{ flexShrink: 0 }} />
          <div>
            <div style={{ fontSize: 14, fontWeight: 600, color: C.darkBrown }}>Start a new plan</div>
            <div style={{ fontSize: 12, color: C.brown, marginTop: 2 }}>Clear your previous answers and begin fresh.</div>
          </div>
        </button>
      </div>
    </>;
  }

  return <>
    <div style={{ textAlign: 'center', marginBottom: 32 }}>
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 0 }}>
        <Brandmark size={36} color={C.lightGreen} />
      </div>
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '2px', color: C.green, marginTop: 16, marginBottom: 10 }}>
        PRECISION NUTRITION
      </div>
      <h1 style={{ fontFamily: F.h, fontSize: 34, fontWeight: 500, color: C.darkBrown, lineHeight: 1.1, margin: '0 0 12px', textWrap: 'balance' }}>
        Let's build the plan your body's been waiting for.
      </h1>
      <p style={{ fontSize: 14, color: C.brown, lineHeight: 1.6, maxWidth: 440, margin: '0 auto 28px' }}>
        A few questions about your goals, your lifestyle, and what you actually want to eat. Then we match every meal to you.
      </p>
    </div>
    <div style={{ marginBottom: 28 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: C.darkBrown, marginBottom: 14 }}>What's on the other side.</div>
      {[{ t: 'Eat like you have a nutritionist', d: 'Your goals, your lifestyle, and your health priorities shape every meal.' },
      { t: 'Eat like you have a private chef', d: 'A rotating menu that changes every week. 9,000+ recipes and counting.' },
      { t: 'Eat like you have it figured out', d: 'Five minutes. No prep. No decisions. Just done.' },
      ].map((o, i) => <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 12 }}><Check size={14} color={C.green} strokeWidth={2.5} style={{ marginTop: 3, flexShrink: 0 }} /><div><div style={{ fontSize: 13, fontWeight: 600, color: C.darkBrown }}>{o.t}</div><div style={{ fontSize: 12, color: C.brown }}>{o.d}</div></div></div>)}
    </div>
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
      <Input placeholder="First Name" value={firstName} onChange={setFirstName} />
      <Input placeholder="Last Name" value={lastName} onChange={setLastName} />
      <Input placeholder="Email Address" value={email} onChange={setEmail} type="email" />
      <Input placeholder="Zip Code" value={zip} onChange={setZip} />
    </div>
    <Btn onClick={handleSignUp}
      disabled={!firstName || !zip || !email}
      style={{ width: '100%' }}>
      Get Started <ChevronRight size={15} />
    </Btn>
    <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginTop: 16 }}>
      {['Gluten-Free', 'No Refined Sugar', 'No Canola Oil'].map((t) => <Tag key={t} color={C.green}><Check size={10} strokeWidth={3} /> {t}</Tag>)}
    </div>
    {zipCodeErrors &&
      <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginTop: 16 }}>
        <p style={{ fontSize: 14, color: C.brown, lineHeight: 1.6, maxWidth: 440, margin: '0 auto 28px' }}>
          {zipCodeErrors}
        </p>
      </div>
    }
  </>;
};

Phase0Landing.propTypes = {
  goPhase: PropTypes.func.isRequired,
};

export default Phase0Landing;
